import { useT } from "../../lib/i18n";
import { cx } from "../../lib/cx";
import { Button } from "./Button";
import { dsCard } from "./styles";

export type SetupStepState = "done" | "next" | "later";

export type SetupStep = {
  id: string;
  state: SetupStepState;
  name: string;
  description: string;
  actionLabel: string;
  onAction?: () => void;
};

type Props = {
  title: string;
  done: number;
  total: number;
  /** Sets the heading id so a dialog can name itself with aria-labelledby. */
  titleId?: string;
  headerAction?: { label: string; onClick?: () => void };
  steps: SetupStep[];
};

function Mark({ state }: { state: SetupStepState }) {
  const t = useT();
  const label =
    state === "done" ? t("ds.setup.done") : state === "next" ? t("ds.setup.next") : t("ds.setup.later");
  return (
    <span
      role="img"
      aria-label={label}
      className={cx(
        "grid size-5 place-items-center rounded-full",
        state === "done" && "bg-ds-teal text-white",
        state === "next" && "border-2 border-solid border-ds-amber bg-transparent",
        state === "later" && "border-2 border-solid border-ds-ink-3 bg-transparent",
      )}
    >
      {state === "done" ? (
        <svg
          viewBox="0 0 24 24"
          className="size-3"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M5 12l5 5 9-10" />
        </svg>
      ) : null}
    </span>
  );
}

/** 34px teal ring beside the title, then done / next / later steps. */
export function SetupChecklist({ title, done, total, titleId, headerAction, steps }: Props) {
  const t = useT();
  const safeTotal = total > 0 ? total : 0;
  const pct = safeTotal === 0 ? 0 : Math.max(0, Math.min(100, (done / safeTotal) * 100));
  return (
    <section data-ds="" data-testid="ds-setup" className={cx(dsCard, "flex h-full flex-col")} aria-label={title}>
      <header className="mb-ds-head flex items-center justify-between gap-2.5">
        <h2 id={titleId} className="m-0 font-ds text-ds-card font-semibold text-ds-ink">{title}</h2>
        <span className="flex items-center gap-2.5">
          <span className="relative size-[34px] shrink-0">
            <svg viewBox="0 0 34 34" className="block size-full" role="img" aria-label={t("ds.setup.progress", { done, total: safeTotal })}>
              <circle cx="17" cy="17" r="13" fill="none" stroke="var(--ds-hair)" strokeWidth="3" />
              <circle
                cx="17"
                cy="17"
                r="13"
                fill="none"
                stroke="var(--ds-teal)"
                strokeWidth="3"
                strokeLinecap="round"
                pathLength="100"
                strokeDasharray={`${pct} 100`}
                transform="rotate(-90 17 17)"
              />
            </svg>
            <span className="absolute inset-0 grid place-items-center text-ds-badge-sm font-normal text-ds-ink">
              {done}/{safeTotal}
            </span>
          </span>
          {headerAction ? (
            <Button variant="link" onClick={headerAction.onClick}>
              {headerAction.label}
            </Button>
          ) : null}
        </span>
      </header>
      <div>
        {steps.map((step) => (
          <div
            key={step.id}
            data-testid={`ds-setup-${step.id}`}
            data-state={step.state}
            className="grid min-h-ds-row-setup grid-cols-[22px_minmax(0,1fr)_auto] items-center gap-3"
          >
            <Mark state={step.state} />
            <div className="min-w-0">
              <b className="block text-ds-item font-semibold text-ds-ink">{step.name}</b>
              <small className="mt-0.5 block text-ds-meta font-normal text-ds-ink-3">
                {step.description}
              </small>
            </div>
            <Button variant="secondary" onClick={step.onAction}>
              {step.actionLabel}
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}
